import React, {useEffect, useState} from 'react';
import {Button, Descriptions, DescriptionsProps, Divider, Input, Modal, Radio} from 'antd';
import {useMediaQuery} from "react-responsive";
import dayjs from 'dayjs';
import {formatCurrency, formatPhoneNumber, getSubscriptionStatusStr} from '../../../util/format';
import { formatKorDate } from '../../../util/date';
import { CashTx } from '../../../types/CashTx';
import CashDescription from '../CashDescription/CashDescription';
import AccountNumberRender from "../CashTable/AccountNumberRender";
import StableInput from "../../common/StableInput/StableInput";

interface CashApprovePopupProps {
  isModalOpen?: boolean,
  handleOk?: (e: React.MouseEvent<HTMLButtonElement>) => void,
  handleReject?: (e: React.MouseEvent<HTMLButtonElement>) => void,
  handleCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void,
  handleEdit: ({ data, reason, reasonCode, accountName }: { data: CashTx, reason?: string, reasonCode?: string, accountName?: string }) => void,
  selectedRecord: CashTx,
  selectedAction?: string
  onChangeReason: (reason: string) => void,
	onChangeReasonCode: (reasonCode: string) => void,
	onChangeAccountName: (accountName: string) => void,
}

const CashApprovePopup = ({
  isModalOpen,
  handleOk,
  handleReject,
  handleCancel,
  handleEdit,
  selectedRecord,
  selectedAction,
  onChangeReason,
  onChangeReasonCode,
  onChangeAccountName,
}: CashApprovePopupProps) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' })
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' })
	const [reason, setReason] = useState(selectedRecord.reason);
	const [reasonCode, setReasonCode] = useState(selectedRecord.reasonCode);
	const [accountName, setAccountName] = useState(selectedRecord.accountName);

  const requestUser = selectedRecord.requestType === 'deposit' ? selectedRecord.toUserName : selectedRecord.fromUserName;
  const requestUserId = selectedRecord.requestType === 'deposit' ? selectedRecord.toUserId : selectedRecord.fromUserId;
  const requestUserPhone = selectedRecord.requestType === 'deposit' ? selectedRecord.toUserPhone : selectedRecord.fromUserPhone;

  const isDone = selectedRecord.status === 'done';
  const isCancel = selectedRecord.status === 'cancel';
  const isFromUserOrToUserUpdate = selectedRecord.updateUser === selectedRecord.fromUserId || selectedRecord.updateUser === selectedRecord.toUserId;

	useEffect(() => {
		setReason(selectedRecord.reason);
		setReasonCode(selectedRecord.reasonCode || 'none');
    setAccountName(selectedRecord.accountName);
	}, [selectedRecord]);

  const getDotColor = () => {
    if (isCancel && isFromUserOrToUserUpdate) return 'orange';
    if (isCancel && !isFromUserOrToUserUpdate) return 'red';
    if (isDone) return 'green';
    return '';
  }

  const getStatusStr = () => {
    if (isCancel && isFromUserOrToUserUpdate) return <span><span style={{ color: getDotColor(), marginRight: '4px'}}>●</span>취소</span>;
    if (isCancel && !isFromUserOrToUserUpdate) return <span><span style={{ color: getDotColor(), marginRight: '4px'}}>●</span>거절</span>;
    if (isDone) return <span><span style={{ color: getDotColor(), marginRight: '4px'}}>●</span>승인</span>;
    return '미확인';
  };

  const items: DescriptionsProps['items'] = [
    {
      label: '요청자',
      children: requestUser,
    },
    {
      label: '연락처',
      span: 'filled',
      children: formatPhoneNumber(requestUserPhone),
    },
    {
      label: '상태',
      span: 'filled',
      children: getStatusStr(),
      contentStyle: { fontWeight: 'bold', backgroundColor: selectedRecord.status === 'hold' ? '#D5E6FD': '' },
    },
    {
      label: '구분',
      span: 'filled',
      children: (selectedRecord.requestType === 'deposit') ? '충전' : '출금',
      contentStyle: (selectedRecord.requestType === 'deposit') ? {
        color: '#1677FF',
        fontWeight: 'bold'
      } : {
        color: 'red',
        fontWeight: 'bold'
      },
    },
    {
      label: '금액',
      span: 'filled',
      children: formatCurrency(Number(selectedRecord.amount), 'KRW'),
      contentStyle: { fontWeight: 'bold' }
    },
    {
      label: '입금자명',
      span: 'filled',
      contentStyle: { fontWeight: 'bold' },
      children: selectedRecord.status === 'hold'
        ? selectedRecord.accountName
        : (
          <StableInput
            placeholder="입금자명"
            value={accountName}
            allowClear
            onChange={(e) => onChangeAccountName(e.target.value)}
          />
        ) ,
    },
    {
      label: '사유',
      span: 'filled',
      children: (
	      <StableInput
		      placeholder="사유"
		      value={reason}
		      allowClear
		      onChange={(e) => onChangeReason(e.target.value)}
	      />
      )
    },
  ];

	if (selectedRecord.status !== 'hold') {
		items.push( {
			label: 'reason code',
			span: 'filled',
			children: (
				<Radio.Group
					value={reasonCode}
					options={[
						{ value: 'none', label: '없음' },
						{ value: 'first_balju', label: '발주지원금' },
						{ value: 'test_user', label: '테스트' },
					]}
					onChange={(e) => onChangeReasonCode(e.target.value)}
				/>
			),
		})
	}

  if (selectedRecord.requestType === 'withdrawal') {
    items.push({
      label: '계좌정보',
      children: <AccountNumberRender text={selectedRecord.accountNumber!} record={
        {
          accountBank: selectedRecord.accountBank!,
          accountName: selectedRecord.accountName!
        }
      } />,
    });
  }

  const okText = (selectedAction === 'approve') ? '승인' : '거절';
  const okButtonColor = (selectedAction === 'approve') ? '#1677FF' : 'red';
  const title = (selectedAction === 'approve') ? '캐쉬충전 승인' : '캐쉬충전 거절';

  const getFormattedDate = (dateString: string) => `${formatKorDate(dateString)} ${dayjs(dateString).format('HH:mm')}`;

  return (
    <Modal
      title={<div>{title}</div>}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={okText}
      okButtonProps={{ style: { backgroundColor: okButtonColor } }}
      cancelText="닫기"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '20px' }}>
	        {selectedRecord.status === 'hold' && (
		        <>
			        <div>
				        <Button color="danger" variant="solid" onClick={handleReject}>
					        거절
				        </Button>
			        </div>

			        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
				        <Button key="back" onClick={handleCancel}>
					        닫기
				        </Button>
				        <Button key="submit" type="primary" onClick={handleOk}>
					        승인
				        </Button>
			        </div>
		        </>
	        )}
	        {selectedRecord.status !== 'hold' && (
						<>
							<div>
								<Button color="default" variant="dashed" onClick={() => handleEdit({ data: selectedRecord, reason, reasonCode, accountName })}>
									수정
								</Button>
							</div>

			        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
				        <Button key="back" onClick={handleCancel}>
					        닫기
				        </Button>
			        </div>
						</>
	        )}
        </div>
      }
    >
      <h4>캐쉬현황</h4>
      <CashDescription userId={requestUserId} style={{
        marginTop: '20px',
        marginBottom: '30px'
      }} />
      {selectedRecord.subscriptionId && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {selectedRecord.subscriptionStatus && <div>{`구독 상태: ${getSubscriptionStatusStr(selectedRecord.subscriptionStatus)}`}</div>}
          {selectedRecord.subscriptionExpireDate && <div>{`만료일시: ${dayjs(selectedRecord.subscriptionExpireDate).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm')}`}</div>}
        </div>
      )}
      <Divider/>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
      }}>
        <h4 style={{ margin: 0 }}>요청내용</h4>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div>등록: {getFormattedDate(selectedRecord.txDate)}</div>
          <div>{selectedRecord.status !== 'hold' && <>처리: {getFormattedDate(selectedRecord.updateDate)}</>}</div>
        </div>
      </div>
      <Descriptions
        bordered
        column={(isTabletOrMobile && isPortrait) ? 1 : 2}
        size="small"
        items={items}
      />
    </Modal>
  );
};

export default CashApprovePopup;
